<template>
  <div>
    <div class="row">
      <div class="col-10">
        <OrderSummaryWidget :gridHeight="250" />
      </div>
      <div class="col-2">
        <AnalyticsWidget title="Detailed Reports" subTitle="Click to access the detailed reports" :gridHeight="250">
          <div class="p-2">
            <AnalyticsReportLink href="/analytics/reports/orders/detailedsummary/glass">Sales - Glass Summary Report</AnalyticsReportLink>
            <AnalyticsReportLink href="/analytics/reports/orders/detailedsummary/services">Sales - Services Summary Report</AnalyticsReportLink>
            <AnalyticsReportLink href="/analytics/reports/orders/ordervaluegroups">Order Value Summary Report</AnalyticsReportLink>
            <AnalyticsReportLink href="/analytics/reports/orders/orderitems">Order Items Report</AnalyticsReportLink>
          </div>
        </AnalyticsWidget>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <OrderBreakdownWidget :gridHeight="260" />
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <OrderDespatchBreakdownWidget :gridHeight="260" />
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <DashboardTabbedContentBox :gridHeight="680" large>
          <template v-slot:commoncontent>
            <div class="dashboard-filter-container">
              <div class="row no-gutters">
                <div class="col-6">
                  <ReportFilterPopover
                    headerTitle="Order Value Filter"
                    :showApplyButton="true"
                    :showResetButton="true"
                    :appendToBody="true"
                    alignment="left"
                    placement="bottom-end"
                    @apply="applyOrderValueFilter"
                    @reset="resetOrderValueFilter">
                    <template v-slot:popover>
                      <ReportDateRangeSelect 
                        :startDate="editingOrderValueFilter.startDate"
                        :endDate="editingOrderValueFilter.endDate"
                        @updateStartDate="editingOrderValueFilter.startDate = $event"
                        @updateEndDate="editingOrderValueFilter.endDate = $event"
                      />
                    </template>
                    <template v-slot:filterDisplay>
                      <div>
                        <i class="far fa-calendar mr-2"></i>{{appliedOrderValueFilter.startDate | formatDate}} - {{appliedOrderValueFilter.endDate | formatDate}}
                      </div>                
                    </template>
                  </ReportFilterPopover>
                </div>
                <div class="col-6 text-right">
                  <AnalyticsReportLink :href="`/analytics/reports/orders/ordervaluegroups#${fullReportTabId}`">View Full Report</AnalyticsReportLink>
                </div>
              </div>
            </div>
          </template>
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-order-glass" @show="setFullReportTab('tab-ordervalue-glass')">
              <i class="tabbedbox-tab-icon fas fa-layer-group"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Glass</div>
                <div class="tabbedbox-tab-subtitle">Order Value Chart</div>
              </div>
            </Tab>
            <Tab tabid="tab-order-glassservice" @show="setFullReportTab('tab-ordervalue-glassService')">
              <i class="tabbedbox-tab-icon fas fa-tools"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Glass Service</div>
                <div class="tabbedbox-tab-subtitle">Order Value Chart</div>
              </div>
            </Tab>
            <Tab tabid="tab-order-other" @show="setFullReportTab('tab-ordervalue-other')">
              <i class="tabbedbox-tab-icon fas fa-inventory"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Other</div>
                <div class="tabbedbox-tab-subtitle">Order Value Chart</div>
              </div>
            </Tab>
            <Tab tabid="tab-order-stocktype" @show="setFullReportTab('tab-ordervalue-stocktype')">
              <i class="tabbedbox-tab-icon fas fa-clipboard-list-check"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Stock Type</div>
                <div class="tabbedbox-tab-subtitle">Order Value Chart</div>
              </div>
            </Tab>
            <Tab tabid="tab-order-pricegroup" @show="setFullReportTab('tab-ordervalue-pricegroup')">
              <i class="tabbedbox-tab-icon fas fa-money-check-edit-alt"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Price Group</div>
                <div class="tabbedbox-tab-subtitle">Order Value Chart</div>
              </div>
            </Tab>
            <Tab tabid="tab-order-meterage" @show="setFullReportTab('tab-ordervalue-squaremeterage')">
              <i class="tabbedbox-tab-icon fas fa-ruler"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Square Meterage</div>
                <div class="tabbedbox-tab-subtitle">Order Value Chart</div>
              </div>
            </Tab>
            <Tab tabid="tab-order-contact" @show="setFullReportTab('tab-ordervalue-contact')">
              <i class="tabbedbox-tab-icon fas fa-user"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Contact</div>
                <div class="tabbedbox-tab-subtitle">Order Value Chart</div>
              </div>
            </Tab>
          </template>
          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-order-glass" class="tabpage-padding-2">
              <OrderValueChartWidget
                :type="OrderValueChartTypes.orderValueGlass"
                :gridHeight="500"
                :embedded="true"
                :showList="true"
                :dateRangeType="ChartDateRangeTypes.custom"
                :startDate="appliedOrderValueFilter.startDate"
                :endDate="appliedOrderValueFilter.endDate"
                :showDateFilter="false"
                @updateEditingStartDate="editingOrderValueFilter.startDate = $event"
                @updateEditingEndDate="editingOrderValueFilter.endDate = $event"
                @dateApplied="applyOrderValueFilter"
                @dateReset="resetOrderValueFilter"
              />
            </TabPage>
            <TabPage tabid="tab-order-glassservice" class="tabpage-padding-2">
              <OrderValueChartWidget
                :type="OrderValueChartTypes.orderValueGlassService"
                :gridHeight="500"
                :embedded="true"
                :showList="true"
                :dateRangeType="ChartDateRangeTypes.custom"
                :startDate="appliedOrderValueFilter.startDate"
                :endDate="appliedOrderValueFilter.endDate"
                :showDateFilter="false"
                @updateEditingStartDate="editingOrderValueFilter.startDate = $event"
                @updateEditingEndDate="editingOrderValueFilter.endDate = $event"
                @dateApplied="applyOrderValueFilter"
                @dateReset="resetOrderValueFilter"
              />
            </TabPage>
            <TabPage tabid="tab-order-other" class="tabpage-padding-2">
              <OrderValueChartWidget
                :type="OrderValueChartTypes.orderValueOther"
                :gridHeight="500"
                :embedded="true"
                :showList="true"
                :dateRangeType="ChartDateRangeTypes.custom"
                :startDate="appliedOrderValueFilter.startDate"
                :endDate="appliedOrderValueFilter.endDate"
                :showDateFilter="false"
                @updateEditingStartDate="editingOrderValueFilter.startDate = $event"
                @updateEditingEndDate="editingOrderValueFilter.endDate = $event"
                @dateApplied="applyOrderValueFilter"
                @dateReset="resetOrderValueFilter"
              />
            </TabPage>
            <TabPage tabid="tab-order-stocktype" class="tabpage-padding-2">
              <OrderValueChartWidget
                :type="OrderValueChartTypes.orderValueStockType"
                :gridHeight="500"
                :embedded="true"
                :showList="true"
                :dateRangeType="ChartDateRangeTypes.custom"
                :startDate="appliedOrderValueFilter.startDate"
                :endDate="appliedOrderValueFilter.endDate"
                :showDateFilter="false"
                @updateEditingStartDate="editingOrderValueFilter.startDate = $event"
                @updateEditingEndDate="editingOrderValueFilter.endDate = $event"
                @dateApplied="applyOrderValueFilter"
                @dateReset="resetOrderValueFilter"
              />
            </TabPage>
            <TabPage tabid="tab-order-pricegroup" class="tabpage-padding-2">
              <OrderValueChartWidget
                :type="OrderValueChartTypes.orderValuePriceGroup"
                :gridHeight="500"
                :embedded="true"
                :showList="true"
                :dateRangeType="ChartDateRangeTypes.custom"
                :startDate="appliedOrderValueFilter.startDate"
                :endDate="appliedOrderValueFilter.endDate"
                :showDateFilter="false"
                @updateEditingStartDate="editingOrderValueFilter.startDate = $event"
                @updateEditingEndDate="editingOrderValueFilter.endDate = $event"
                @dateApplied="applyOrderValueFilter"
                @dateReset="resetOrderValueFilter"
              />
            </TabPage>
            <TabPage tabid="tab-order-meterage" class="tabpage-padding-2">
              <OrderValueChartWidget
                :type="OrderValueChartTypes.orderUnitsGlass"
                :gridHeight="500"
                :embedded="true"
                :showList="true"
                :dateRangeType="ChartDateRangeTypes.custom"
                :startDate="appliedOrderValueFilter.startDate"
                :endDate="appliedOrderValueFilter.endDate"
                :showDateFilter="false"
                @updateEditingStartDate="editingOrderValueFilter.startDate = $event"
                @updateEditingEndDate="editingOrderValueFilter.endDate = $event"
                @dateApplied="applyOrderValueFilter"
                @dateReset="resetOrderValueFilter"
              />
            </TabPage>
            <TabPage tabid="tab-order-contact" class="tabpage-padding-2">
              <OrderValueChartWidget
                :type="OrderValueChartTypes.orderValueContact"
                :gridHeight="500"
                :embedded="true"
                :showList="true"
                :dateRangeType="ChartDateRangeTypes.custom"
                :startDate="appliedOrderValueFilter.startDate"
                :endDate="appliedOrderValueFilter.endDate"
                :showDateFilter="false"
                @updateEditingStartDate="editingOrderValueFilter.startDate = $event"
                @updateEditingEndDate="editingOrderValueFilter.endDate = $event"
                @dateApplied="applyOrderValueFilter"
                @dateReset="resetOrderValueFilter"
              />
            </TabPage>
          </template>
        </DashboardTabbedContentBox>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <DashboardTabbedContentBox :gridHeight="630" large>
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-order-uservalue">
              <i class="tabbedbox-tab-icon fas fa-user"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Order Value</div>
                <div class="tabbedbox-tab-subtitle">User Chart</div>
              </div>
            </Tab>
            <Tab tabid="tab-order-usercount">
              <i class="tabbedbox-tab-icon fas fa-list-ol"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Order Count</div>
                <div class="tabbedbox-tab-subtitle">User Chart</div>
              </div>
            </Tab>
            <Tab tabid="tab-order-userduration">
              <i class="tabbedbox-tab-icon fas fa-stopwatch"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Entry Duration</div>
                <div class="tabbedbox-tab-subtitle">User Chart</div>
              </div>
            </Tab>
            <Tab tabid="tab-order-accountmanager-value">
              <i class="tabbedbox-tab-icon fas fa-users"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Order Value</div>
                <div class="tabbedbox-tab-subtitle">Account Manager Chart</div>
              </div>
            </Tab>
            <Tab tabid="tab-order-accountmanager-count">
              <i class="tabbedbox-tab-icon fas fa-list-ol"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Order Count</div>
                <div class="tabbedbox-tab-subtitle">Account Manager Chart</div>
              </div>
            </Tab>


          </template>
          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-order-uservalue" class="tabpage-padding-2">
              <OrderValueChartWidget
                :type="OrderValueChartTypes.orderValueUserValue"
                :gridHeight="500"
                :embedded="true"
                :showList="true"
                :dateRangeType="ChartDateRangeTypes.custom"
                :startDate="appliedOrderValueFilter.startDate"
                :endDate="appliedOrderValueFilter.endDate"
                :showDateFilter="true"
                @updateEditingStartDate="editingOrderValueFilter.startDate = $event"
                @updateEditingEndDate="editingOrderValueFilter.endDate = $event"
                @dateApplied="applyOrderValueFilter"
                @dateReset="resetOrderValueFilter"
              />
            </TabPage>
            <TabPage tabid="tab-order-usercount" class="tabpage-padding-2">
              <OrderValueChartWidget
                :type="OrderValueChartTypes.orderValueUserCount"
                :gridHeight="500"
                :embedded="true"
                :showList="true"
                :dateRangeType="ChartDateRangeTypes.custom"
                :startDate="appliedOrderValueFilter.startDate"
                :endDate="appliedOrderValueFilter.endDate"
                :showDateFilter="true"
                @updateEditingStartDate="editingOrderValueFilter.startDate = $event"
                @updateEditingEndDate="editingOrderValueFilter.endDate = $event"
                @dateApplied="applyOrderValueFilter"
                @dateReset="resetOrderValueFilter"
              />
            </TabPage>
            <TabPage tabid="tab-order-userduration" class="tabpage-padding-2">
              <OrderValueChartWidget
                :type="OrderValueChartTypes.orderValueUserDuration"
                :gridHeight="500"
                :embedded="true"
                :showList="true"
                :dateRangeType="ChartDateRangeTypes.custom"
                :startDate="appliedOrderValueFilter.startDate"
                :endDate="appliedOrderValueFilter.endDate"
                :showDateFilter="true"
                @updateEditingStartDate="editingOrderValueFilter.startDate = $event"
                @updateEditingEndDate="editingOrderValueFilter.endDate = $event"
                @dateApplied="applyOrderValueFilter"
                @dateReset="resetOrderValueFilter"
              />
            </TabPage>
            <TabPage tabid="tab-order-accountmanager-value" class="tabpage-padding-2">
              <OrderValueChartWidget
                :type="OrderValueChartTypes.orderValueAccountManagerValue"
                :gridHeight="500"
                :embedded="true"
                :showList="true"
                :dateRangeType="ChartDateRangeTypes.custom"
                :startDate="appliedOrderValueFilter.startDate"
                :endDate="appliedOrderValueFilter.endDate"
                :showDateFilter="true"
                @updateEditingStartDate="editingOrderValueFilter.startDate = $event"
                @updateEditingEndDate="editingOrderValueFilter.endDate = $event"
                @dateApplied="applyOrderValueFilter"
                @dateReset="resetOrderValueFilter"
              />
            </TabPage>
            <TabPage tabid="tab-order-accountmanager-count" class="tabpage-padding-2">
              <OrderValueChartWidget
                :type="OrderValueChartTypes.orderValueAccountManagerCount"
                :gridHeight="500"
                :embedded="true"
                :showList="true"
                :dateRangeType="ChartDateRangeTypes.custom"
                :startDate="appliedOrderValueFilter.startDate"
                :endDate="appliedOrderValueFilter.endDate"
                :showDateFilter="true"
                @updateEditingStartDate="editingOrderValueFilter.startDate = $event"
                @updateEditingEndDate="editingOrderValueFilter.endDate = $event"
                @dateApplied="applyOrderValueFilter"
                @dateReset="resetOrderValueFilter"
              />
            </TabPage>
          </template>
        </DashboardTabbedContentBox>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <DashboardTabbedContentBox :gridHeight="630" large>
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-order-daily">
              <i class="tabbedbox-tab-icon fas fa-calendar-day"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Daily</div>
                <div class="tabbedbox-tab-subtitle">Order Value Chart</div>
              </div>
            </Tab>
            <Tab tabid="tab-order-monthly">
              <i class="tabbedbox-tab-icon fas fa-calendar-alt"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Monthly</div>
                <div class="tabbedbox-tab-subtitle">Order Value Chart</div>
              </div>
            </Tab>
            <Tab tabid="tab-order-yearly">
              <i class="tabbedbox-tab-icon fas fa-calendar"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Yearly</div>
                <div class="tabbedbox-tab-subtitle">Order Value Chart</div>
              </div>
            </Tab>
            <Tab tabid="tab-order-duration-daily">
              <i class="tabbedbox-tab-icon fas fa-clock"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Daily</div>
                <div class="tabbedbox-tab-subtitle">Order Duration Chart</div>
              </div>
            </Tab>
            <Tab tabid="tab-order-estimate-daily">
              <i class="tabbedbox-tab-icon fas fa-user-clock"></i>
              <div class="tabbedbox-tab-info">
                <div class="tabbedbox-tab-title">Daily</div>
                <div class="tabbedbox-tab-subtitle">Order Estimates Chart</div>
              </div>
            </Tab>
          </template>
          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-order-daily" class="tabpage-padding-0">
              <OrderDailyChartWidget :gridHeight="540" :embedded="true" />
            </TabPage>
            <TabPage tabid="tab-order-monthly" class="tabpage-padding-0">
              <OrderMonthlyChartWidget :gridHeight="540" :embedded="true" />
            </TabPage>
            <TabPage tabid="tab-order-yearly" class="tabpage-padding-0">
              <OrderYearlyChartWidget :gridHeight="540" :embedded="true" />
            </TabPage>
            <TabPage tabid="tab-order-duration-daily" class="tabpage-padding-0">
              <OrderDurationDailyChartWidget :gridHeight="540" :embedded="true" />
            </TabPage>
            <TabPage tabid="tab-order-estimate-daily" class="tabpage-padding-0">
              <OrderEstimateDailyChartWidget :gridHeight="540" :embedded="true" />
            </TabPage>
          </template>
        </DashboardTabbedContentBox>
      </div>
    </div>
  </div>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import DashboardTitle from '@/components/dashboard/dashboardTitle'
import DashboardContentBox from '@/components/dashboard/dashboardContentBox'
import DashboardTabbedContentBox from '@/components/dashboard/dashboardTabbedContentBox'
import AnalyticsDashboardHeader from '@/modules/intelligence/components/analyticsDashboardHeader'
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsReportLink from '@/modules/intelligence/components/analyticsReportLink'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'

import OrderSummaryWidget from '@/modules/intelligence/dashboardWidgets/orderSummaryWidget'
import OrderBreakdownWidget from '@/modules/intelligence/dashboardWidgets/orderBreakdownWidget'
import OrderDespatchBreakdownWidget from '@/modules/intelligence/dashboardWidgets/orderDespatchBreakdownWidget'
import OrderDailyChartWidget from '@/modules/intelligence/dashboardWidgets/orderDailyChartWidget'
import OrderMonthlyChartWidget from '@/modules/intelligence/dashboardWidgets/orderMonthlyChartWidget'
import OrderYearlyChartWidget from '@/modules/intelligence/dashboardWidgets/orderYearlyChartWidget'
import OrderValueChartWidget from '@/modules/intelligence/dashboardWidgets/orderValueChartWidget'
import OrderDurationDailyChartWidget from '@/modules/intelligence/dashboardWidgets/orderDurationDailyChartWidget'
import OrderEstimateDailyChartWidget from '@/modules/intelligence/dashboardWidgets/orderEstimateDailyChartWidget'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import { ChartDateRangeTypes } from '@/modules/intelligence/types'

export default {
  name: 'AnalyticsOrderDashboard',
  components: {
    DashboardTitle,
    DashboardContentBox,
    DashboardTabbedContentBox,
    AnalyticsDashboardHeader,
    AnalyticsWidget,
    AnalyticsReportLink,
    AnalyticsDashboardValue,
    OrderSummaryWidget,
    OrderBreakdownWidget,
    OrderDespatchBreakdownWidget,
    OrderDailyChartWidget,
    OrderMonthlyChartWidget,
    OrderYearlyChartWidget,
    OrderValueChartWidget,
    OrderDurationDailyChartWidget,
    OrderEstimateDailyChartWidget,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    filter: Object
  },
  data() {
    return {
      dayOrderValueSummary: {},

      defaultOrderValueFilter: {    
        startDate: moment().local().startOf('month').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString()
      },
      editingOrderValueFilter: {},
      appliedOrderValueFilter: {},

      // Tab ID of the full summary report page
      // So we can link to the correct tab
      fullReportTabId: 'tab-ordervalue-glass'
    }
  },
  created() {
    this.OrderValueChartTypes = OrderValueChartWidget.OrderValueChartTypes
    this.ChartDateRangeTypes = ChartDateRangeTypes
  },
  mounted() {
    this.editingOrderValueFilter = _.clone(this.defaultOrderValueFilter)
    this.appliedOrderValueFilter = _.clone(this.defaultOrderValueFilter)

    this.requestSummary()
    this.subscribeEndpoint('/orders', () => {
      this.requestSummary()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  methods: {
    applyOrderValueFilter() {
      this.appliedOrderValueFilter = _.clone(this.editingOrderValueFilter)

      this.requestSummary()
    },
    resetOrderValueFilter() {
      this.editingOrderValueFilter = _.clone(this.defaultOrderValueFilter)
      this.appliedOrderValueFilter = _.clone(this.defaultOrderValueFilter)

      this.requestSummary()
    },
    requestSummary() {
      let startDate = moment(this.appliedOrderValueFilter.startDate).local().startOf('day').toDate().toISOString()
      let endDate = moment(this.appliedOrderValueFilter.endDate).local().endOf('day').toDate().toISOString()

      this.$apiService.analyticsService.getOrdersValueFullSummary({ startDate: startDate, endDate: endDate })
      .then(response => {
        this.dayOrderValueSummary = response.data
      })
    },
    setFullReportTab(tabId) {
      this.fullReportTabId = tabId
    }
  }
  
}
</script>